import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, type Profile } from "@/lib/supabase";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (params: {
    email: string;
    password: string;
    fullName: string;
    jobTitle?: string;
  }) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      console.warn("[auth] profile fetch failed", error.message);
      return null;
    }
    return data as Profile | null;
  };

  useEffect(() => {
    let mounted = true;

    // Safety net — if Supabase request hangs for any reason, bail out after 6s
    const safetyTimer = setTimeout(() => {
      if (mounted) {
        console.warn("[auth] session fetch timed out, continuing without session");
        setLoading(false);
      }
    }, 6000);

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        if (data.session?.user) {
          const p = await fetchProfile(data.session.user.id);
          if (mounted) setProfile(p);
        }
      } catch (err) {
        console.warn("[auth] getSession failed", err);
      } finally {
        if (mounted) {
          clearTimeout(safetyTimer);
          setLoading(false);
        }
      }
    })();

    // CRITICAL: The onAuthStateChange callback must NOT be async and must NOT
    // await any Supabase calls. During page refresh, Supabase's _initialize()
    // holds the internal lock and calls _notifyAllSubscribers(), which waits
    // for all subscribers to return. If this callback awaits fetchProfile()
    // (which calls supabase.from().select()), that call internally waits for
    // getSession() → initializePromise → the same lock → deadlock.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        fetchProfile(newSession.user.id).then((p) => {
          setProfile(p);
        });
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      sub.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signUp = async ({
    email,
    password,
    fullName,
    jobTitle,
  }: {
    email: string;
    password: string;
    fullName: string;
    jobTitle?: string;
  }) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          job_title: jobTitle ?? "Staff Member",
          role: "staff",
        },
      },
    });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshProfile = async () => {
    if (!session?.user) return;
    const p = await fetchProfile(session.user.id);
    setProfile(p);
  };

  const requestPasswordReset = async (email: string) => {
    const base = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${base}/reset-password`,
    });
    return { error: error?.message ?? null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error?.message ?? null };
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      requestPasswordReset,
      updatePassword,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, profile, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
